import { useState } from 'react'
import testimonialLines from '#app/assets/images/decorative/testimonial-lines.svg'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './ui/button.tsx'
import { InputText } from './ui/input-text.tsx'
import { TestimonialsCarousel } from './ui/testimonials-carousel.tsx'

interface Testimonial {
	id: string
	quote: string
	author: string
	location: string
}

interface TestimonialsSectionProps {
	className?: string
	testimonials?: Testimonial[]
}

const defaultTestimonials: Testimonial[] = [
	{
		id: '1',
		quote:
			'El verdadero valor de 100prevalor está en su compromiso con la sostenibilidad. Nos han ayudado a implementar prácticas que no solo protegen el ambiente, sino que también han mejorado nuestra productividad.',
		author: 'María González',
		location: 'Productora Agrícola, Mendoza',
	},
	{
		id: '2',
		quote:
			'Trabajar con 100prevalor ha transformado nuestra manera de entender la economía circular. Sus proyectos nos han permitido generar valor compartido con toda la comunidad.',
		author: 'Carlos Rodríguez',
		location: 'Cooperativa, San Juan',
	},
	{
		id: '3',
		quote:
			'La asesoría de 100prevalor fue clave para obtener nuestras certificaciones de sostenibilidad. Su enfoque integral nos acompaña en cada paso hacia un futuro más responsable.',
		author: 'Ana Martínez',
		location: 'Empresa Familiar, Córdoba',
	},
]

export function TestimonialsSection({
	className,
	testimonials = defaultTestimonials,
}: TestimonialsSectionProps) {
	const [email, setEmail] = useState('')

	const handleEmailSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (email.trim()) {
			console.log('Email submitted:', email)
			// TODO: Implement email subscription logic
			setEmail('')
		}
	}

	return (
		<div className="px-4 py-0">
			<section
				className={cn(
					'relative overflow-hidden rounded-[24px] bg-green-50 px-4 py-16 md:px-0 md:py-[100px]',
					className,
				)}
			>
				{/* Background decorative elements */}
				<div className="absolute inset-0 overflow-hidden">
					{/* Gradiente superior izquierda */}
					<div className="absolute top-2 left-[-140px]">
						<div className="h-[580.11px] w-[965.24px] origin-top-left rotate-[14.74deg] bg-blue-100 opacity-50 mix-blend-multiply blur-[100px]"></div>
					</div>
					{/* Gradiente inferior derecha */}
					<div className="absolute top-[460px] left-[603px]">
						<div className="h-[580.11px] w-[965.24px] origin-top-left rotate-[14.74deg] bg-blue-100 opacity-50 mix-blend-multiply blur-[100px]"></div>
					</div>
					{/* Líneas decorativas SVG */}
					<div className="absolute left-0 h-1/2 w-full">
						<img
							src={testimonialLines}
							alt=""
							className="absolute top-0 left-0 h-full w-full object-cover opacity-30"
						/>
					</div>
				</div>
				<div className="relative z-10 mx-auto max-w-[300px] md:container md:max-w-none">
					<div className="flex flex-col items-center gap-16">
						{/* Header */}
						<div className="flex w-full flex-col items-center gap-8 text-center md:max-w-[862px]">
							<h2 className="font-parkinsans text-2xl leading-8 font-medium text-blue-800 md:text-4xl md:leading-[64px] lg:text-[48px]">
								El verdadero valor está en lo que logramos juntos
							</h2>
							<p className="font-inter text-base leading-[26px] font-normal text-neutral-600 md:text-lg md:leading-8 md:font-medium">
								Conocé qué dicen los productores que trabajan con nosotros
							</p>
						</div>
						{/* Testimonials Carousel */}
						<div className="w-full">
							{/* Desktop version - with side controls */}
							<div className="hidden md:block">
								<TestimonialsCarousel
									testimonials={testimonials}
									autoPlay={true}
									autoPlayInterval={6000}
									controlsPosition="sides"
								/>
							</div>

							{/* Mobile version - with bottom controls */}
							<div className="block md:hidden">
								<TestimonialsCarousel
									testimonials={testimonials}
									autoPlay={true}
									autoPlayInterval={6000}
									controlsPosition="bottom"
								/>
							</div>
						</div>
						{/* Email Subscription Form */}
						<div className="w-full">
							{/* Desktop version */}
							<div className="hidden md:block">
								<div className="flex w-full flex-col items-center gap-8">
									<form
										onSubmit={handleEmailSubmit}
										className="flex w-full flex-col items-center gap-3 md:flex-row md:items-center md:justify-center"
									>
										<div className="w-full max-w-[300px] md:w-[385px] md:max-w-none">
											<InputText
												type="email"
												value={email}
												onChange={(e) => setEmail(e.target.value)}
												label="Email"
												labelPosition="internal"
												placeholder="Email"
												required
												size="l"
											/>
										</div>
										<Button
											type="submit"
											size="l"
											variant="primary"
											appearance="solid"
											className="hidden md:inline-flex"
										>
											Quiero unirme
										</Button>
									</form>
								</div>
							</div>

							{/* Mobile version - Figma design */}
							<div className="block md:hidden">
								<div className="relative flex w-full shrink-0 flex-col content-stretch items-center justify-start gap-8">
									<form
										onSubmit={handleEmailSubmit}
										className="relative flex w-full max-w-[300px] shrink-0 flex-col content-stretch items-center justify-start gap-3"
									>
										<div className="relative flex h-10 max-h-10 min-h-10 w-full shrink-0 content-stretch items-center justify-start rounded-[8px] bg-white">
											<InputText
												type="email"
												value={email}
												onChange={(e) => setEmail(e.target.value)}
												label="Email"
												labelPosition="internal"
												placeholder="Email"
												required
												size="m"
											/>
										</div>
										<button
											type="submit"
											className="relative box-border flex w-full shrink-0 content-stretch items-center justify-center gap-2 overflow-clip rounded-[12px] bg-blue-500 px-4 py-3 transition-colors duration-300 hover:bg-blue-600"
										>
											<div className="font-parkinsans relative shrink-0 text-center text-[14px] leading-[0] font-medium text-nowrap text-white not-italic">
												<p className="leading-[16px] whitespace-pre">
													Quiero unirme
												</p>
											</div>
										</button>
									</form>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}

export default TestimonialsSection
